import React, { useState } from "react";
import { api, API_V1, tokenStore } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Upload, FileSpreadsheet, ShieldX, CheckCircle2, AlertCircle, Loader2, Download } from "lucide-react";
import { toast } from "sonner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import axios from "axios";

const TEMPLATE_COLUMNS = [
  "employee_id","first_name","last_name","email","designation","job_grade_band","job_level",
  "department","sub_department","business_unit","location_city","location_country",
  "employment_type","date_of_joining","reporting_manager_employee_id",
  "matrix_manager_employee_id","hrbp_employee_id","pronouns",
];

export default function AdminImport({ embedded = false }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [result, setResult] = useState(null);

  const downloadTemplate = () => {
    const csv = TEMPLATE_COLUMNS.join(",") + "\n";
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "axia_employees_template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadSample = () => {
    const sample =
      TEMPLATE_COLUMNS.join(",") +
      "\nE001,Jordan,Lee,jordan.lee@example.com,Senior Engineer,M3,IC4,Engineering,,Tech,Bengaluru,IN,FTE,2024-02-10,,,,\n" +
      "E002,Casey,Patel,casey.patel@example.com,Engineer,M2,IC3,Engineering,,Tech,Bengaluru,IN,FTE,2024-04-01,E001,,,\n" +
      "E003,Mei,Chen,mei.chen@example.com,Salary Officer,M3,IC4,Finance,,Finance,Bengaluru,IN,FTE,2024-04-01,,,,\n";
    const blob = new Blob([sample], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "axia_sample_employees.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const dryRun = async () => {
    if (!file) {
      toast.error("Pick a CSV file first");
      return;
    }
    setPreviewing(true);
    setPreview(null);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API_V1}/import/employees/dry-run`, fd, {
        headers: { Authorization: `Bearer ${tokenStore.getAccess()}` },
      });
      setPreview(r.data);
      if (r.data.error_rows === 0) toast.success(`Preview ready · ${r.data.valid_rows} valid rows`);
      else toast.warning(`${r.data.error_rows} rows have errors. Fix and re-upload.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import preview failed");
    } finally {
      setPreviewing(false);
    }
  };

  const commit = async () => {
    if (!preview || preview.error_rows > 0) return;
    setCommitting(true);
    try {
      const r = await api.post("/import/employees/commit", { rows: preview.rows.map((r) => r.data) });
      setResult(r.data);
      toast.success(`Imported · ${r.data.inserted} added, ${r.data.updated} updated`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally {
      setCommitting(false);
    }
  };

  return (
    <div className={embedded ? "space-y-6" : "max-w-7xl space-y-6"} data-testid="admin-import-page">
      {!embedded && (
        <div>
          <div className="axia-overline">Admin · Bulk Import</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mt-1.5">Bring your team into Axia</h1>
          <p className="text-sm text-slate-500 mt-2 max-w-2xl">
            Upload a CSV of your employees. Axia will validate row-by-row, detect cycles & orphans, and reject any column whose name suggests compensation.
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="axia-card p-6 lg:col-span-2 space-y-5" data-testid="upload-card">
          <div className="flex items-center justify-between">
            <div className="font-display font-semibold text-slate-900">Upload CSV</div>
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={downloadTemplate} data-testid="download-template">
                <Download className="w-3.5 h-3.5 mr-1.5" /> Template
              </Button>
              <Button variant="ghost" size="sm" onClick={downloadSample} data-testid="download-sample">
                <Download className="w-3.5 h-3.5 mr-1.5" /> Sample
              </Button>
            </div>
          </div>
          <label className="block">
            <div className="border-2 border-dashed border-slate-200 rounded-lg p-8 text-center cursor-pointer hover:border-slate-400 transition" data-testid="upload-dropzone">
              <FileSpreadsheet className="w-7 h-7 mx-auto text-slate-400" />
              <div className="text-sm font-medium text-slate-900 mt-3">{file ? file.name : "Click to choose a CSV file"}</div>
              <div className="text-xs text-slate-500 mt-1">UTF-8 encoded, first row = headers</div>
              <input
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                data-testid="upload-input"
              />
            </div>
          </label>
          <div className="flex gap-2 justify-end">
            <Button onClick={dryRun} disabled={!file || previewing} data-testid="dry-run-btn">
              {previewing && <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />} Validate (dry-run)
            </Button>
            <Button onClick={commit} disabled={!preview || preview.error_rows > 0 || committing} variant="default" className="bg-emerald-700 hover:bg-emerald-600" data-testid="commit-btn">
              {committing && <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />} Commit import
            </Button>
          </div>
        </div>

        <div className="axia-card p-6 border-l-4 border-l-rose-300" data-testid="comp-warning">
          <div className="flex items-center gap-2">
            <ShieldX className="w-4 h-4 text-rose-600" />
            <div className="font-display font-semibold text-slate-900">Compensation columns are rejected</div>
          </div>
          <p className="text-sm text-slate-600 mt-2 leading-relaxed">
            Columns whose names contain <span className="font-mono text-xs">salary</span>, <span className="font-mono text-xs">ctc</span>, <span className="font-mono text-xs">bonus</span>, <span className="font-mono text-xs">equity</span>, <span className="font-mono text-xs">pay</span>, or similar will be refused at upload time. Axia is a performance product, not a payroll product.
          </p>
        </div>
      </div>

      {preview && (
        <div className="axia-card overflow-hidden" data-testid="preview-card">
          <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-4 flex-wrap">
            <Stat label="Total" value={preview.total_rows} />
            <Stat label="Valid" value={preview.valid_rows} positive />
            <Stat label="Errors" value={preview.error_rows} negative />
            <Stat label="Orphans" value={preview.orphans?.length || 0} />
            <Stat label="Cycles" value={preview.cycles_detected?.length || 0} />
          </div>
          {preview.rejected_columns?.length > 0 && (
            <div className="bg-rose-50 px-5 py-3 text-sm text-rose-700 border-b border-rose-200">
              Compensation columns rejected: {preview.rejected_columns.join(", ")}
            </div>
          )}
          <div className="max-h-[480px] overflow-auto">
            <Table>
              <TableHeader className="bg-slate-50 sticky top-0">
                <TableRow>
                  <TableHead className="w-12 text-[10px] uppercase tracking-wide text-slate-500">#</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wide text-slate-500">ID</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wide text-slate-500">Name</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wide text-slate-500">Email</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wide text-slate-500">Designation</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wide text-slate-500">Manager</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wide text-slate-500">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {preview.rows.map((row) => (
                  <TableRow key={row.row_number} data-testid={`preview-row-${row.row_number}`}>
                    <TableCell className="font-mono text-xs text-slate-500">{row.row_number}</TableCell>
                    <TableCell className="font-mono text-xs">{row.data.employee_id}</TableCell>
                    <TableCell className="text-sm text-slate-800">{row.data.first_name} {row.data.last_name}</TableCell>
                    <TableCell className="font-mono text-xs">{row.data.email}</TableCell>
                    <TableCell className="text-sm">{row.data.designation || "—"}</TableCell>
                    <TableCell className="font-mono text-xs">{row.data.reporting_manager_employee_id || "—"}</TableCell>
                    <TableCell>
                      {row.errors.length > 0 ? (
                        <div className="space-y-0.5">
                          {row.errors.map((e, i) => (
                            <div key={i} className="inline-flex items-center gap-1 text-xs text-rose-700"><AlertCircle className="w-3 h-3" /> {e}</div>
                          ))}
                        </div>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs text-emerald-700"><CheckCircle2 className="w-3 h-3" /> ok</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {result && (
        <div className="axia-card p-6 bg-emerald-50/50 border-emerald-200" data-testid="import-result">
          <div className="flex items-center gap-2 text-emerald-800">
            <CheckCircle2 className="w-5 h-5" />
            <div className="font-display font-semibold">Import complete</div>
          </div>
          <div className="text-sm text-emerald-900 mt-2">
            {result.inserted} new employees added · {result.updated} existing employees updated · job <span className="font-mono">{result.job_id}</span>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, positive, negative }) {
  return (
    <div>
      <div className="axia-overline">{label}</div>
      <div className={`font-display text-xl font-bold mt-0.5 ${positive ? "text-emerald-700" : negative ? "text-rose-700" : "text-slate-900"}`}>{value}</div>
    </div>
  );
}
